import React, { useCallback, useEffect, useRef, useState } from 'react';

import KK from 'constants/dom/keyboardKey.const';
import IP from 'constants/form/inputParser.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import asArray from 'utils/arrays/asArray.util';
import compareArrays from 'utils/arrays/compareArrays.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import focusElement from 'utils/dom/focusElement.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import asObject from 'utils/objects/asObject.util';
import deRefAt from 'utils/objects/deRefAt.util';
import parseCode from 'utils/parsers/parseCode.util';
import parseDigit from 'utils/parsers/parseDigit.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import isDigit from 'utils/predicates/number/isDigit.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import asInput from 'utils/strings/asInput.util';

import CN from './CodeInput.module.scss';

const CTAG = 'CodeInput';

const LENGTH = 6;

const setDigit = ({ code, index, digit }) => {
  const newCode = [...code];
  newCode[index] = isDigit(digit) ? digit : null;
  return newCode;
};

const delDigit = ({ code, index }) => {
  const newCode = [...code];
  for (let i = index; i < newCode.length; i += 1) {
    newCode[i] = null;
  }
  return newCode;
};

const CodeInput = ({
  autofocus,
  children, // removed from extra, so it is not passed to the <input>
  data,
  disabled,
  value: valueFromProps,
  length = LENGTH,
  name,
  onChange = alwaysNoop,
  onClick = alwaysNoop,
  onKeyDown = alwaysNoop,
  tag: tagFromProps,
  ...extra
}) => {
  const tag = fromAnyToTrimmed(tagFromProps);

  // if not autofocus, the -1 will select no element to focus
  const [focusIndex, setFocusIndex] = useStateWhileMounted(autofocus ? 0 : -1);
  const inputsRef = useRef(Array(length).fill(null));

  const [code, setCode] = useState(Array(LENGTH).fill(null));

  const { val: isDirty, inv: isClean, set: setDirty } = useTip(false);
  const { val: isError, inv: isValid, set: setError } = useTip(false);

  // eslint-disable-next-line no-return-assign
  const setRefFor = useCallback((index) => (el) => void (inputsRef.current[index] = el), []);

  const prepare = useCallback(
    (type, index, ev) => {
      const { number: digit } = parseDigit({ value: fromEventToValue(ev) });

      const updateDigit = type === 'del' ? delDigit : setDigit;
      const value = updateDigit({ index, code, digit });

      const dirty = !compareArrays(value, code);
      const parsed = parseCode({ value, length: LENGTH });

      return { ...parsed, tag, parser: IP.code, name, dirty, ev, event: ev };
    },
    [code, name, tag],
  );

  const reconcile = useCallback(
    ({ result, prepared }) => {
      const { error, value, dirty } = asObject(result);

      const newValue = isMissing(value) ? prepared.raw : value;
      const newDirty = isMissing(dirty) ? newValue !== valueFromProps : dirty;

      setCode(newValue);
      setDirty(newDirty);
      setError(error);

      return { value: newValue, dirty: newDirty, error };
    },
    [setDirty, setError, valueFromProps],
  );

  const handle = useCallback(
    (name, type, index, fn, ev) => {
      const prepared = prepare(type, index, ev);
      const result = call(`${CTAG}.${name}`, fn, prepared);
      return reconcile({ result, prepared });
    },
    [prepare, reconcile],
  );

  useEffect(
    //
    () => void focusElement(deRefAt(focusIndex, inputsRef)),
    [autofocus, focusIndex],
  );

  return (
    <div data-bem="CodeInput" data-tag={tag} data-field-clean={isClean} data-field-dirty={isDirty} data-field-error={isError} data-field-valid={isValid} className={CN.component}>
      {asArray(code).map((digit, index) => {
        const handleClick = (ev) => handle(`${CTAG}.onClick`, 'set', index, onClick, ev);

        const handleChange = (ev) => {
          handle(`${CTAG}.onChange`, 'set', index, onChange, ev);
          setFocusIndex(index + 1 < code.length ? index + 1 : index);
        };

        const handleKeyDown = (ev) => {
          const { key } = ev;
          if (key !== KK.backspace && key !== KK.delete) return;

          handle(`${CTAG}.onKeyDown`, 'del', index, onKeyDown, ev);
          setFocusIndex(index > 1 ? index - 1 : 0);
        };

        return (
          <input
            {...extra}
            data-bem="CodeInput__digit"
            data-index={index}
            data-tag={tag}
            tabIndex={0}
            key={createNodeKey({ index, tag: tagFromProps })}
            inputMode="decimal"
            type="text"
            value={asInput(isDigit(code[index]) ? code[index] : '')}
            className={CN.input}
            ref={setRefFor(index)}
            onChange={handleChange}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          />
        );
      })}
    </div>
  );
};

// noinspection JSUnusedGlobalSymbols
export default CodeInput;
